import File from "Entity/File";
import Product from "Entity/Product";
import Choice from "Entity/Property/Choice";

export type FolderLevel = {
    slug?: string
    type?: string
    choices: string[]
}

export type FolderGrouping = {
    primary?: FolderLevel
    secondary?: FolderLevel
}

// One leaf of the tree, carrying whatever the modal needs to render it (a File, a product, a PDF card…).
export type PreviewEntry<P = unknown> = {
    key: string
    position: number
    path: string[]
    name: string
    thumbnailSrc?: string
    payload: P
}

export type Folder<P = unknown> = {
    name: string
    key: string
    folders: Folder<P>[]
    entries: PreviewEntry<P>[]
    count: number
}

export type PreviewTree<P> = {
    root: Folder<P>
    entries: PreviewEntry<P>[]
}

type RawLeaf<P> = {
    path: string[]
    name: string
    thumbnailSrc?: string
    payload: P
}

const UNCATEGORIZED = 'uncategorized'

// PHP's array_filter() and `?:` treat '0' as empty, so the zip never creates a folder named '0'.
const isPhpTruthy = (value: string): boolean => value !== '' && value !== '0'

const camelSlug = (slug: string): string => slug.replace(/-([a-z0-9])/g, match => match[1].toUpperCase())

const sanitizeFolderName = (name: string): string => {
    const cleaned = Array.from(name.replace(/[/\\?%*:|"<>]/g, '-'))
        .filter(character => {
            const code = character.codePointAt(0) ?? 0

            return code > 31 && code !== 127
        })
        .join('')
        .replace(/^[ .\t\n\r]+|[ .\t\n\r]+$/g, '')
        .replace(/-{2,}/g, '-')

    return Array.from(cleaned).slice(0, 100).join('').trim()
}

const getFolderNames = (
    item: File | Product,
    level: FolderLevel,
    choiceLabel: (choice: Choice) => string,
): string[] => {
    if (!level.slug) {
        return []
    }

    const value = item[camelSlug(level.slug)]
    let values: { id?: string, label: string }[] = []

    if (level.type === 'choice') {
        const choices: Choice[] = (Array.isArray(value) ? value : [value])
            .filter((choice: unknown) => choice !== null && typeof choice === 'object')
        values = choices.map(choice => ({id: String(choice.id), label: choiceLabel(choice)}))
    } else if (level.type === 'text') {
        // Translatable text is serialized as an object of translations; the zip reads the untranslated
        // column, which is empty for those, so they never become folders.
        const text = Array.isArray(value) ? value[0] : value
        values = typeof text === 'string' && isPhpTruthy(text) ? [{label: text}] : []
    }

    if (level.choices.length > 0) {
        values = values.filter(({id, label}) =>
            (id !== undefined && level.choices.includes(id)) || level.choices.includes(label)
        )
    }

    return Array.from(new Set(values.map(({label}) => sanitizeFolderName(label)).filter(isPhpTruthy)))
}

// Mirrors BimboShareService::getGroupingPaths() in bimbo-back (src/Service/BimboShareService.php:731).
// Keep the two in step, or the preview list stops matching the folders of the downloaded zip.
export const getFolderPaths = (
    item: File | Product,
    grouping: FolderGrouping,
    choiceLabel: (choice: Choice) => string,
): string[][] => {
    const {primary, secondary} = grouping

    if (!primary && !secondary) {
        return [[]]
    }

    const primaryNames = primary ? getFolderNames(item, primary, choiceLabel) : []
    const secondaryNames = secondary ? getFolderNames(item, secondary, choiceLabel) : []

    if (primaryNames.length === 0) {
        return [[UNCATEGORIZED]]
    }

    if (secondaryNames.length === 0) {
        return primaryNames.map(primaryName => [primaryName])
    }

    return primaryNames.flatMap(primaryName => secondaryNames.map(secondaryName => [primaryName, secondaryName]))
}

const compareNames = (a: string, b: string): number => a.localeCompare(b, undefined, {
    numeric: true,
    sensitivity: 'base',
})

const sortFolder = <P>(folder: Folder<P>, isRoot: boolean): void => {
    folder.folders.sort((a, b) => {
        if (isRoot && (a.name === UNCATEGORIZED) !== (b.name === UNCATEGORIZED)) {
            return a.name === UNCATEGORIZED ? 1 : -1
        }

        return compareNames(a.name, b.name)
    })
    folder.entries.sort((a, b) => compareNames(a.name, b.name))
    folder.folders.forEach(child => sortFolder(child, false))
}

const flattenFolder = <P>(folder: Folder<P>): PreviewEntry<P>[] => [
    ...folder.folders.flatMap(flattenFolder),
    ...folder.entries,
]

const assemble = <P>(leaves: RawLeaf<P>[], sortTree: boolean): PreviewTree<P> => {
    const root: Folder<P> = {name: '', key: '', folders: [], entries: [], count: 0}

    leaves.forEach(leaf => {
        let folder = root
        folder.count++

        leaf.path.forEach(name => {
            let child = folder.folders.find(candidate => candidate.name === name)

            if (!child) {
                child = {name, key: `${folder.key}/${name}`, folders: [], entries: [], count: 0}
                folder.folders.push(child)
            }

            child.count++
            folder = child
        })

        folder.entries.push({
            key: `${folder.key}#${folder.entries.length}`,
            position: 0,
            path: leaf.path,
            name: leaf.name,
            thumbnailSrc: leaf.thumbnailSrc,
            payload: leaf.payload,
        })
    })

    if (sortTree) {
        sortFolder(root, true)
    }

    const entries = flattenFolder(root)
    entries.forEach((entry, position) => {
        entry.position = position
    })

    return {root, entries}
}

const smallThumb = (thumbnail?: string): string | undefined => thumbnail?.replaceAll('__SIZING__', 'small')

// One section (a bucket's files or a catalog's products), grouped like the zip. Payload = the item itself.
export const buildFolderTree = (
    items: (File | Product)[],
    grouping: FolderGrouping,
    choiceLabel: (choice: Choice) => string,
    getName: (item: File | Product) => string,
): PreviewTree<{ item: File | Product }> => {
    const leaves: RawLeaf<{ item: File | Product }>[] = []

    items.forEach(item => {
        getFolderPaths(item, grouping, choiceLabel).forEach(path => {
            leaves.push({path, name: getName(item), thumbnailSrc: smallThumb(item.thumbnail), payload: {item}})
        })
    })

    // Without grouping the zip is flat, and the list keeps the order the share editor shows.
    return assemble(leaves, Boolean(grouping.primary || grouping.secondary))
}

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'bmp', 'svg']

export const isImageName = (name: string): boolean =>
    IMAGE_EXTENSIONS.includes(name.split('.').pop()?.toLowerCase() ?? '')

// An external file uploaded from the user's computer. previewUrl is an object URL, present only while the
// blob is still in memory this session; a saved share reloads without it, so those fall back to an icon.
export type ExternalFileInput = {
    name: string
    path: string
    isImage: boolean
    previewUrl?: string
}

export type ExternalPayload = { kind: 'external', file: ExternalFileInput }

// External files carry no property tags, so the tree is a flat list navigated on its own.
export const buildExternalTree = (files: ExternalFileInput[]): PreviewTree<ExternalPayload> =>
    assemble(files.map(file => ({
        path: [],
        name: file.name,
        thumbnailSrc: file.previewUrl,
        payload: {kind: 'external' as const, file},
    })), false)

export type ZipPayload =
    | { kind: 'file', file: File }
    | { kind: 'productPdf', product: Product }
    | ExternalPayload

export type ZipInput = {
    buckets: { [slug: string]: File[] }
    catalogs: { [slug: string]: Product[] }
    externalFiles: ExternalFileInput[]
    grouping: FolderGrouping
    productWithPDF: boolean
    productUniqueDirectory: boolean
    choiceLabel: (choice: Choice) => string
    productName: (product: Product) => string
    productSlug: (product: Product) => string
}

// The whole share as one merged tree, matching the downloaded zip (BimboShareService::generateZip(),
// bimbo-back/src/Service/BimboShareService.php:156): files and product files share the property folders,
// product PDF cards sit under the product's folder, external files stay at the root.
export const buildZipTree = (input: ZipInput): PreviewTree<ZipPayload> => {
    const {grouping, choiceLabel, productWithPDF, productUniqueDirectory} = input
    const leaves: RawLeaf<ZipPayload>[] = []

    Object.values(input.buckets).forEach(files => {
        files.forEach(file => {
            getFolderPaths(file, grouping, choiceLabel).forEach(path => {
                leaves.push({
                    path, name: file.name, thumbnailSrc: smallThumb(file.thumbnail), payload: {kind: 'file', file},
                })
            })
        })
    })

    Object.values(input.catalogs).forEach(products => {
        products.forEach(product => {
            const productFiles = Object.values(product.files ?? {}).flat()

            getFolderPaths(product, grouping, choiceLabel).forEach(path => {
                productFiles.forEach(file => {
                    leaves.push({
                        path, name: file.name, thumbnailSrc: smallThumb(file.thumbnail), payload: {kind: 'file', file},
                    })
                })

                if (productWithPDF) {
                    const dir = productUniqueDirectory ? path : [...path, input.productSlug(product)]
                    leaves.push({
                        path: dir, name: `${input.productSlug(product)}.pdf`, payload: {kind: 'productPdf', product},
                    })
                }
            })
        })
    })

    input.externalFiles.forEach(file => {
        leaves.push({path: [], name: file.name, thumbnailSrc: file.previewUrl, payload: {kind: 'external', file}})
    })

    return assemble(leaves, Boolean(grouping.primary || grouping.secondary))
}
